import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ExpertTipFilters } from '../types/expert-tip';

const STORAGE_KEY = 'sparkadmin_expert_tip_filters';

const DEFAULT_FILTERS: ExpertTipFilters = {
  expertId: '',
  status: '',
  topicIds: [],
  page: 1,
  pageSize: 25,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<ExpertTipFilters> {
  const filters: Partial<ExpertTipFilters> = {};

  const expertId = searchParams.get('expertId');
  if (expertId) filters.expertId = expertId;

  const status = searchParams.get('status');
  if (status && ['published', 'unpublished', 'archived'].includes(status)) {
    filters.status = status as ExpertTipFilters['status'];
  }

  const topicIds = searchParams.get('topicIds');
  if (topicIds) {
    filters.topicIds = topicIds
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const sortBy = searchParams.get('sortBy');
  if (sortBy && ['title', 'sortOrder', 'updatedAt'].includes(sortBy)) {
    filters.sortBy = sortBy as ExpertTipFilters['sortBy'];
  }

  const sortOrder = searchParams.get('sortOrder');
  if (sortOrder && ['asc', 'desc'].includes(sortOrder)) {
    filters.sortOrder = sortOrder as ExpertTipFilters['sortOrder'];
  }

  return filters;
}

function filtersToSearchParams(filters: ExpertTipFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.expertId) params.set('expertId', filters.expertId);
  if (filters.status) params.set('status', filters.status);
  if (filters.topicIds.length > 0) params.set('topicIds', filters.topicIds.join(','));
  if (filters.page !== 1) params.set('page', filters.page.toString());
  if (filters.sortBy !== 'updatedAt') params.set('sortBy', filters.sortBy);
  if (filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);

  return params;
}

function loadFiltersFromStorage(): Partial<ExpertTipFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: ExpertTipFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

export function useExpertTipFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<ExpertTipFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    const storageFilters = loadFiltersFromStorage();
    return {
      ...DEFAULT_FILTERS,
      ...storageFilters,
      ...urlFilters,
    };
  });

  const updateFilter = useCallback(
    (key: keyof ExpertTipFilters, value: ExpertTipFilters[keyof ExpertTipFilters]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        if (key !== 'page') {
          updated.page = 1;
        }
        const params = filtersToSearchParams(updated);
        setSearchParams(params, { replace: true });
        saveFiltersToStorage(updated);
        return updated;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
    saveFiltersToStorage(DEFAULT_FILTERS);
  }, [setSearchParams]);

  const hasActiveFilters =
    filters.expertId !== '' || filters.status !== '' || filters.topicIds.length > 0;

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
  };
}
